import { useState, useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { FEE_MGMT_COLUMNS, ROW_NUMBER_COL } from '../constants/Columns'
import { createOverrideSheet } from '../../../store/reducers/feeManagement/feeManagementSlice'
import { mapGridDataToApi } from '../constants/DataMapping'
import { useGridData } from '../context/GridDataContext'
import { exportToXLSX } from '../../../common/utils/xlsxExport'

export const useGridState = ({
  validatedRows,
  gridApiRef,
  overrideType,
  overrideName,
  fieldsToOverride,
  refreshValidation,
  resetValidation,
}) => {
  const dispatch = useDispatch()
  const { mappingData } = useGridData()
  const [headerDeleteActive, setHeaderDeleteActive] = useState(false)
  const [filteredRows, setFilteredRows] = useState([])
  const [activeFilters, setActiveFilters] = useState([])
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
  const [canSaveOverride, setCanSaveOverride] = useState(false)
  const [currentEditingRow, setCurrentEditingRow] = useState(null)

  const currentDataRef = useRef(null)
  const prevFilterModelRef = useRef('{}')
  const columnStateRef = useRef(null)

  // Get the create override state from Redux
  const {
    isLoading: isSaving,
    error: saveError,
    success: saveSuccess,
  } = useSelector((state) => state.feeManagement.createOverride)

  // Update current data reference when validated rows change
  useEffect(() => {
    if (validatedRows?.length) {
      currentDataRef.current = validatedRows
      setFilteredRows(validatedRows)
    }
  }, [validatedRows])

  // Handle cell editing events to preserve column state
  const handleCellEditingStarted = useCallback((params) => {
    // Save the current column state when editing starts
    if (params.api) {
      columnStateRef.current = params.api.getColumnState()
    }
  }, [])

  const handleCellEditingStopped = useCallback((params) => {
    // Restore the column state when editing stops
    if (params.api && columnStateRef.current) {
      setTimeout(() => {
        params.api.applyColumnState({
          state: columnStateRef.current,
          applyOrder: true,
        })
      }, 0)
    }
  }, [])

  // Function to restore column state to initial state
  const restoreColumnState = useCallback(() => {
    if (gridApiRef.current && columnStateRef.current) {
      gridApiRef.current.applyColumnState({
        state: columnStateRef.current,
        applyOrder: true,
      })
    }
  }, [gridApiRef])

  const onGridDataChange = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    let hasError = false
    let hasAtLeastOneRow = false
    const currentData = []

    gridApiRef.current.forEachNode((node) => {
      currentData.push(node.data)

      // Skip rows marked for deletion
      if (node.data.userMarkedForDelete || node.data.isDuplicateRow) {
        return
      }

      hasAtLeastOneRow = true

      // Exit early if we find any errors
      if (Object.keys(node.data.validationErrors || {}).length > 0) {
        hasError = true
        return false // Break the loop
      }
    })

    // Update current data reference
    currentDataRef.current = currentData

    setCanSaveOverride(hasAtLeastOneRow && !hasError)
  }, [gridApiRef])

  // Function to save initial column state during grid ready
  const initializeGridState = useCallback(() => {
    if (gridApiRef.current) {
      columnStateRef.current = gridApiRef.current.getColumnState()
      onGridDataChange()
    }
  }, [gridApiRef, onGridDataChange])

  // Function to get current grid data
  const getCurrentGridData = useCallback(
    (filterRowsMarkedForDelete = false) => {
      if (!gridApiRef.current) {
        return currentDataRef.current || []
      }

      const currentData = []
      if (filterRowsMarkedForDelete) {
        gridApiRef.current.forEachNode((node) => {
          if (!node.data.userMarkedForDelete) {
            currentData.push(node.data)
          }
        })
      } else {
        gridApiRef.current.forEachNode((node) => {
          currentData.push(node.data)
        })
      }

      return currentData
    },
    [gridApiRef],
  )

  // Function to save grid data
  const saveGridData = useCallback(async () => {
    if (!gridApiRef.current || !overrideName || !overrideType || !fieldsToOverride?.length) {
      return { success: false, error: 'Missing required data. Contact support if the issue persists.' }
    }

    try {
      // Get current grid data
      const gridData = getCurrentGridData(true).filter((row) => !row.userMarkedForDelete && !row.isDuplicateRow)
      // Map the grid data to API format using the context function
      const mappedData = mapGridDataToApi(gridData, mappingData, fieldsToOverride, overrideType)
      // Create the override using the already mapped data
      const result = await dispatch(
        createOverrideSheet({
          description: overrideName,
          entityTypes: mappedData.entityTypes,
          dataTypes: mappedData.dataTypes,
          overrides: mappedData.overrides,
          active: false,
        }),
      ).unwrap()

      return {
        success: true,
        result: {
          ...result,
          sheetId: result.sheet_id,
        },
      }
    } catch (error) {
      return { success: false, error }
    }
  }, [dispatch, gridApiRef, overrideName, overrideType, fieldsToOverride, getCurrentGridData, mappingData])

  const handleToggleDelete = useCallback(
    (rowNumbers, shouldMark, recordEditCb) => {
      if (!filteredRows?.length || !gridApiRef?.current) {
        return
      }

      // First update the filtered rows to include the new delete status
      const updatedRows = filteredRows.map((row) => {
        if (rowNumbers.includes(row.rowNumber)) {
          const newRow = { ...row, userMarkedForDelete: shouldMark }
          return newRow
        }
        return row
      })
      setFilteredRows(updatedRows)

      // Get the rows that will be updated
      const rowsWithUpdates = rowNumbers.map((rowNumber) => {
        return filteredRows.find((r) => r.rowNumber === rowNumber)
      })

      // Create updated data objects with the new userMarkedForDelete value
      const updatedData = rowsWithUpdates.map((row) => ({
        ...row,
        userMarkedForDelete: shouldMark,
      }))

      // Save a copy of the original rows for undo
      const originalData = rowsWithUpdates.map((row) => ({ ...row }))

      // Function to apply deletion via transaction - works for both initial action and redo
      const applyDeletion = () => {
        gridApiRef.current.applyTransactionAsync(
          {
            update: updatedData,
          },
          () => {
            // After transaction completes, update UI and validate
            onGridDataChange()
            refreshValidation()
          },
        )
      }

      // Apply the initial transaction
      applyDeletion()

      // Register with undo/redo system, using transactions for consistency
      recordEditCb(
        rowNumbers,
        originalData,
        // This is the redo callback that will use transactions
        () => {
          gridApiRef.current.applyTransactionAsync(
            {
              update: updatedData,
            },
            () => {
              onGridDataChange()
              refreshValidation()
            },
          )
        },
      )
    },
    [filteredRows, gridApiRef, onGridDataChange, refreshValidation],
  )

  const handleHeaderDeleteToggle = useCallback(
    (recordEditCb) => {
      if (!gridApiRef?.current) {
        return
      }

      const visibleRows = []
      gridApiRef.current.forEachNodeAfterFilter((node) => {
        visibleRows.push(node.data)
      })

      if (visibleRows.length === 0) {
        return
      }

      const shouldMark = !headerDeleteActive
      const rowNumbers = visibleRows.map((row) => row.rowNumber)

      setHeaderDeleteActive(shouldMark)
      handleToggleDelete(rowNumbers, shouldMark, recordEditCb)
    },
    [gridApiRef, headerDeleteActive, handleToggleDelete],
  )

  const handleFilterChanged = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    const filterModel = gridApiRef.current.getFilterModel()

    // Compare current filter model with previous one
    const filterModelJSON = JSON.stringify(filterModel)
    if (prevFilterModelRef.current === filterModelJSON) {
      // Skip if filter model hasn't changed to avoid cascading updates
      return
    }
    prevFilterModelRef.current = filterModelJSON

    const newActiveFilters = Object.entries(filterModel).map(([colId, filter]) => {
      const column = gridApiRef.current.getColumnDef(colId)
      return {
        colId,
        columnName: column.headerName || column.field,
        filter,
      }
    })

    // Deep comparison to avoid unnecessary state updates
    if (isEqual(activeFilters, newActiveFilters)) {
      return
    }

    setActiveFilters(newActiveFilters)
  }, [gridApiRef, activeFilters])

  const handleFilteredRowsChange = useCallback((rows) => {
    if (!rows) {
      return
    }
    setFilteredRows(rows)
  }, [])

  const handleRemoveFilter = useCallback(
    (colId) => {
      if (!gridApiRef.current) {
        return
      }

      gridApiRef.current.setFilterModel({
        ...gridApiRef.current.getFilterModel(),
        [colId]: null,
      })

      const column = gridApiRef.current.getColumnDef(colId)
      if (column) {
        gridApiRef.current.destroyFilter(colId)
      }
    },
    [gridApiRef],
  )

  const handleToggleExpand = useCallback((expanded) => {
    setIsSummaryExpanded(expanded)
  }, [])

  const handleValidationFilterChange = useCallback(
    (errorType, isSelected) => {
      if (!gridApiRef.current || !currentDataRef.current) {
        return
      }

      if (isSelected) {
        const filteredData = currentDataRef.current.filter((row) => {
          if (errorType === 'DUPLICATE_ROW') {
            return row.isDuplicateRow
          }

          return Object.values(row.validationErrors || {}).some((error) => error.details && error.details[errorType])
        })

        setFilteredRows(filteredData)
      } else {
        setFilteredRows(currentDataRef.current)
      }
    },
    [gridApiRef, currentDataRef],
  )

  const handleExportCSV = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    // Store the current column state (includes sort)
    const currentColumnState = gridApiRef.current.getColumnState()

    // Apply sort by rowNumber
    gridApiRef.current.applyColumnState({
      state: [{ colId: ROW_NUMBER_COL.columnKey, sort: 'asc' }],
      defaultState: { sort: null },
    })

    // Get current grid columns and filter for exportable ones
    const currentGridColumns = gridApiRef.current.getColumns()?.map(col => col.getColId()) || []
    const exportableColumnKeys = Object.values(FEE_MGMT_COLUMNS)
      .filter((column) =>
        column.context.customGridColumnOptions.includeInCsvExport &&
        currentGridColumns.includes(column.columnKey)
      )
      .map((column) => column.columnKey)

    // Create a map of columns that should have quotes suppressed
    const columnsWithSuppressedQuotes = Object.values(FEE_MGMT_COLUMNS)
      .filter((column) =>
        column.context.customGridColumnOptions.suppressQuotesInCsvExport &&
        currentGridColumns.includes(column.columnKey)
      )
      .reduce((map, column) => {
        map[column.columnKey] = true
        return map
      }, {})

    // Configure export parameters
    const exportParams = {
      columnKeys: exportableColumnKeys,
      fileName: `${overrideName || 'export'}.csv`,
      suppressQuotes: true,
      processCellCallback: (params) => {
        // Skip if no value
        if (params.value === null || params.value === undefined) {
          return ''
        }

        // If this column should have quotes suppressed, return the raw value
        // AG Grid will handle this by not adding quotes when suppressQuotes is set for this cell
        if (columnsWithSuppressedQuotes[params.column.colId]) {
          if (typeof params.value === 'number') {
            return params.value
          }
          return `"${params.value}"`
        }

        // Return value normally for other columns
        return `"${params.value}"`
      },
    }

    // Export the data
    gridApiRef.current.exportDataAsCsv(exportParams)

    // Restore the original column state
    gridApiRef.current.applyColumnState({
      state: currentColumnState,
    })
  }, [gridApiRef, overrideName])

  const handleExportXLSX = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    // Store the current column state (includes sort)
    const currentColumnState = gridApiRef.current.getColumnState()

    // Apply sort by rowNumber
    gridApiRef.current.applyColumnState({
      state: [{ colId: ROW_NUMBER_COL.columnKey, sort: 'asc' }],
      defaultState: { sort: null },
    })

    // Get current grid columns and filter for exportable ones
    const currentGridColumns = gridApiRef.current.getColumns()?.map(col => col.getColId()) || []
    const exportableColumnKeys = Object.values(FEE_MGMT_COLUMNS)
      .filter((column) =>
        column.context.customGridColumnOptions.includeInCsvExport &&
        currentGridColumns.includes(column.columnKey)
      )
      .map((column) => column.columnKey)

    // Create a map of columns that should have quotes suppressed for consistency with CSV
    const columnsWithSuppressedQuotes = Object.values(FEE_MGMT_COLUMNS)
      .filter((column) =>
        column.context.customGridColumnOptions.suppressQuotesInCsvExport &&
        currentGridColumns.includes(column.columnKey)
      )
      .reduce((map, column) => {
        map[column.columnKey] = true
        return map
      }, {})

    // Get current grid data
    const currentData = getCurrentGridData()

    // Process cell callback to match CSV export logic
    const processCellCallback = (params) => {
      // Skip if no value
      if (params.value === null || params.value === undefined) {
        return ''
      }

      // If this column should have quotes suppressed, return the raw value
      if (columnsWithSuppressedQuotes[params.column.colId]) {
        if (typeof params.value === 'number') {
          return params.value
        }
        // For XLSX, we don't need to add quotes as the library handles text formatting
        return params.value
      }

      // Return value normally for other columns
      return params.value
    }

    try {
      // Export to XLSX with all columns as text data type
      exportToXLSX(
        currentData,
        exportableColumnKeys,
        overrideName || 'export',
        processCellCallback,
        FEE_MGMT_COLUMNS
      )
    } catch (error) {
      console.error('Error exporting to XLSX:', error)
    }

    // Restore the original column state
    gridApiRef.current.applyColumnState({
      state: currentColumnState,
    })
  }, [gridApiRef, overrideName, getCurrentGridData])

  // Handle row editing start/stop
  const handleCellValueChanged = useCallback(
    (event) => {
      if (event.data?.rowNumber !== undefined) {
        setCurrentEditingRow(event.data.rowNumber)
      }

      // Save the current column state
      const currentColumnState = gridApiRef.current.getColumnState()

      // Restore column state after cell value changes
      if (gridApiRef.current) {
        setTimeout(() => {
          gridApiRef.current.applyColumnState({
            state: currentColumnState,
            applyOrder: true,
          })
        }, 0)
      }
    },
    [setCurrentEditingRow, gridApiRef],
  )

  // Handle saving from the toolbar
  const handleToolbarValidate = useCallback(() => {
    if (!gridApiRef.current) {
      return
    }

    // Save current column state before validation
    const currentColumnState = gridApiRef.current.getColumnState()

    // Capture the current edited row data before stopping edit
    let rowNode = null
    let currentEditedData = null
    gridApiRef.current.forEachNode((node) => {
      if (node.data?.rowNumber === currentEditingRow) {
        rowNode = node
        currentEditedData = { ...node.data } // Make a copy of the current data
      }
    })

    // Stop editing first
    gridApiRef.current.stopEditing()

    // Verify if data changed after stopEditing
    if (rowNode && currentEditedData) {
      gridApiRef.current.applyTransactionAsync(
        {
          update: [currentEditedData],
        },
        () => {
          refreshValidation()
          // Restore column state after validation
          setTimeout(() => {
            gridApiRef.current.applyColumnState({
              state: currentColumnState,
              applyOrder: true,
            })
          }, 0)
          gridApiRef.current.refreshCells({
            force: true,
            rowNodes: [rowNode],
          })
        },
      )
    }

    // Clear editing state
    setCurrentEditingRow(null)
    resetValidation()
    // Update grid state
    onGridDataChange()
  }, [gridApiRef, currentEditingRow, onGridDataChange, refreshValidation, resetValidation])

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleToolbarValidate()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleToolbarValidate])

  return {
    gridApiRef,
    filteredRows,
    activeFilters,
    isSummaryExpanded,
    canSaveOverride,
    isSaving,
    saveError,
    saveSuccess,
    currentEditingRow,
    handleToggleDelete,
    handleHeaderDeleteToggle,
    handleFilterChanged,
    handleFilteredRowsChange,
    handleRemoveFilter,
    handleToggleExpand,
    handleValidationFilterChange,
    getCurrentGridData,
    saveGridData,
    handleExportCSV,
    handleExportXLSX,
    handleCellValueChanged,
    handleToolbarValidate,
    handleCellEditingStarted,
    handleCellEditingStopped,
    restoreColumnState,
    initializeGridState,
  }
}
